import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Inbox, Lock, LockOpen, Pause, RefreshCw, Shuffle, Trash2, X } from "lucide-react";
import { ApiError } from "@/lib/api";
import {
  type AccountRole,
  useBulkDeleteAccounts,
  useBulkCheck,
  useBulkCheckStatus,
  useBulkPatchAccounts,
  useBulkSweep,
  useDistributeProxies,
  useRebalanceProxiesByCountry,
} from "@/lib/accounts";
import { useProxies } from "@/lib/proxies";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SimpleSelect, type SimpleSelectOption } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/ui/dialog";
import { zSticky } from "@/lib/z";
import { pushToast } from "@/components/Toaster";

/**
 * BulkActionBar — sticky selection toolbar, grouped Role | Proxy | Tag | Danger
 * (DESIGN-SYSTEM §6.1). No mutation fires on a select change — every group has
 * an explicit "Apply" step. Bulk delete goes through a ConfirmDialog with the
 * exact count and reports an undo-hint toast.
 */
const BULK_MAX = 500;
const SWEEP_MAX = 200;
// Average pause between two checks of a bulk "Проверить" (the worker draws
// 40–100 s) — only for the estimate shown before starting.
const CHECK_AVG_SECONDS = 70;

export function BulkActionBar({
  selectedIds,
  onCleared,
}: {
  selectedIds: string[];
  onCleared: () => void;
}) {
  const { t } = useTranslation();
  const proxies = useProxies();
  const patch = useBulkPatchAccounts();
  const del = useBulkDeleteAccounts();
  const rebalance = useRebalanceProxiesByCountry();
  const distribute = useDistributeProxies();
  const bulkSweep = useBulkSweep();
  const bulkCheck = useBulkCheck();
  const checkRunning = useBulkCheckStatus().data?.active ?? false;

  // Staged selections — only mutate on the explicit Apply press.
  const [role, setRole] = useState<AccountRole | "">("");
  const [proxyChoice, setProxyChoice] = useState("");
  const [sourceTag, setSourceTag] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmKick, setConfirmKick] = useState(false);
  const [confirm2fa, setConfirm2fa] = useState(false);
  const [confirmCheck, setConfirmCheck] = useState(false);

  const count = selectedIds.length;
  // The bulk endpoints take a bounded number of accounts per call. "Select
  // all" on a large fleet used to send them all and come back with an English
  // validation error — say the limit here instead.
  const overBulk = count > BULK_MAX;
  const overSweep = count > SWEEP_MAX;
  const busy =
    overBulk ||
    patch.isPending ||
    del.isPending ||
    rebalance.isPending ||
    distribute.isPending ||
    bulkSweep.isPending ||
    bulkCheck.isPending;

  const reportPartial = (updated: number, skipped: number) => {
    if (skipped > 0) {
      pushToast({
        kind: "warning",
        text: t("accounts.bulkbar.partial", { updated, skipped }),
      });
    } else {
      pushToast({ kind: "success", text: t("accounts.bulkbar.applied", { count: updated }) });
      onCleared();
    }
  };

  const applyRole = async () => {
    if (!role) return;
    try {
      const res = await patch.mutateAsync({ account_ids: selectedIds, role });
      setRole("");
      reportPartial(res.updated, res.skipped.length);
    } catch (err) {
      pushToast({
        kind: "error",
        text: err instanceof ApiError ? err.detail : t("accounts.error_unknown"),
      });
    }
  };

  const applyProxy = async () => {
    if (!proxyChoice) return;
    const proxyId = proxyChoice === "__random__" ? null : proxyChoice;
    const random = proxyChoice === "__random__";
    try {
      const res = await patch.mutateAsync({
        account_ids: selectedIds,
        proxy_id: proxyId,
        proxy_random: random,
      });
      setProxyChoice("");
      reportPartial(res.updated, res.skipped.length);
    } catch (err) {
      pushToast({
        kind: "error",
        text: err instanceof ApiError ? err.detail : t("accounts.error_unknown"),
      });
    }
  };

  const onRebalance = async () => {
    try {
      const r = await rebalance.mutateAsync(selectedIds);
      pushToast({
        kind: "success",
        text: t("accounts.bulkbar.rebalance_done", {
          reassigned: r.reassigned,
          already: r.already_matched,
          no_match: r.no_match,
          no_country: r.no_country,
        }),
      });
    } catch (err) {
      pushToast({
        kind: "error",
        text: err instanceof ApiError ? err.detail : t("accounts.error_unknown"),
      });
    }
  };

  const onDistribute = async () => {
    try {
      const r = await distribute.mutateAsync({ account_ids: selectedIds });
      pushToast({
        kind: "success",
        // With dead proxies left out the spread may no longer be one-to-one.
        text: r.dead_proxies_skipped
          ? t("accounts.bulkbar.distribute_done_dead", {
              assigned: r.assigned,
              proxies: r.proxies_used,
              dead: r.dead_proxies_skipped,
            })
          : t("accounts.bulkbar.distribute_done", {
              assigned: r.assigned,
              proxies: r.proxies_used,
            }),
      });
      onCleared();
    } catch (err) {
      pushToast({
        kind: "error",
        text: err instanceof ApiError ? err.detail : t("accounts.error_unknown"),
      });
    }
  };

  const applySource = async () => {
    if (!sourceTag.trim()) return;
    try {
      const res = await patch.mutateAsync({
        account_ids: selectedIds,
        source_label: sourceTag.trim(),
      });
      setSourceTag("");
      reportPartial(res.updated, res.skipped.length);
    } catch (err) {
      pushToast({
        kind: "error",
        text: err instanceof ApiError ? err.detail : t("accounts.error_unknown"),
      });
    }
  };

  const applyInbox = async (enabled: boolean) => {
    try {
      const res = await patch.mutateAsync({
        account_ids: selectedIds,
        inbox_enabled: enabled,
      });
      reportPartial(res.updated, res.skipped.length);
    } catch (err) {
      pushToast({
        kind: "error",
        text: err instanceof ApiError ? err.detail : t("accounts.error_unknown"),
      });
    }
  };

  const applyStatus = async (status: "active" | "paused") => {
    try {
      const res = await patch.mutateAsync({ account_ids: selectedIds, status });
      reportPartial(res.updated, res.skipped.length);
    } catch (err) {
      pushToast({
        kind: "error",
        text: err instanceof ApiError ? err.detail : t("accounts.error_unknown"),
      });
    }
  };

  const onBulkSweep = async (action: "lock_2fa" | "terminate_sessions") => {
    try {
      const r = await bulkSweep.mutateAsync({ account_ids: selectedIds, action });
      pushToast({
        kind: "success",
        text: t("accounts.bulk_sweep.enqueued", { count: r.enqueued, wave: r.wave_size }),
      });
      onCleared();
    } catch (err) {
      pushToast({
        kind: "error",
        text: err instanceof ApiError ? err.detail : t("accounts.error_unknown"),
      });
    }
  };

  const onBulkCheck = async () => {
    try {
      const r = await bulkCheck.mutateAsync(selectedIds);
      const minutes = Math.max(1, Math.round(r.eta_seconds / 60));
      pushToast({
        kind: "success",
        text:
          r.skipped_fresh > 0
            ? t("accounts.bulk_check.enqueued_skipped", {
                count: r.enqueued,
                minutes,
                skipped: r.skipped_fresh,
              })
            : t("accounts.bulk_check.enqueued", { count: r.enqueued, minutes }),
        durationMs: 9000,
      });
      onCleared();
    } catch (err) {
      pushToast({
        kind: "error",
        text: err instanceof ApiError ? err.detail : t("accounts.error_unknown"),
      });
    }
  };

  const onDelete = async () => {
    try {
      const res = await del.mutateAsync(selectedIds);
      pushToast({
        kind: "success",
        text: t("accounts.bulkbar.deleted", { count: res.deleted }),
      });
      onCleared();
    } catch (err) {
      pushToast({
        kind: "error",
        text: err instanceof ApiError ? err.detail : t("accounts.error_unknown"),
      });
    }
  };

  const roleOptions: SimpleSelectOption[] = [
    { value: "scraper", label: t("accounts.role.scraper") },
    { value: "inviter", label: t("accounts.role.inviter") },
    { value: "both", label: t("accounts.role.both") },
  ];
  const proxyOptions: SimpleSelectOption[] = [
    { value: "__random__", label: t("accounts.bulkbar.proxy_random_short") },
    ...(proxies.data ?? []).map((p) => ({ value: p.id, label: p.label })),
  ];

  return (
    <div
      style={{ zIndex: zSticky }}
      className="rounded-xl border border-accent/40 bg-bg-elevated/95 p-3 shadow-md backdrop-blur md:sticky md:top-2"
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="font-medium">
          {t("accounts.bulkbar.selected", { count })}
        </span>

        {/* Role group */}
        <div className="flex max-w-full flex-wrap items-center gap-1.5">
          <span className="text-xs text-fg-muted">{t("accounts.bulkbar.role")}</span>
          <div className="min-w-[10rem] flex-1 sm:w-48 sm:flex-none">
            <SimpleSelect
              options={roleOptions}
              value={role}
              onValueChange={(v) => setRole(v as AccountRole)}
              placeholder={t("accounts.bulkbar.role_pick")}
              disabled={busy}
            />
          </div>
          <Button size="sm" variant="secondary" disabled={!role || busy} onClick={applyRole}>
            {t("accounts.bulkbar.apply")}
          </Button>
        </div>

        {/* Proxy group */}
        <div className="flex max-w-full flex-wrap items-center gap-1.5">
          <span className="text-xs text-fg-muted">{t("accounts.bulkbar.proxy")}</span>
          <div className="min-w-[10rem] flex-1 sm:w-44 sm:flex-none">
            <SimpleSelect
              options={proxyOptions}
              value={proxyChoice}
              onValueChange={setProxyChoice}
              placeholder={t("accounts.bulkbar.proxy_pick")}
              disabled={busy}
            />
          </div>
          <Button size="sm" variant="secondary" disabled={!proxyChoice || busy} onClick={applyProxy}>
            {t("accounts.bulkbar.apply")}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={onDistribute}
            iconLeft={<Shuffle size={15} strokeWidth={2} aria-hidden="true" />}
            title={t("accounts.bulkbar.distribute_hint") ?? ""}
          >
            {t("accounts.bulkbar.distribute")}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={onRebalance}
            iconLeft={<Globe size={15} strokeWidth={2} aria-hidden="true" />}
            title={t("accounts.bulkbar.rebalance_hint") ?? ""}
          >
            {t("accounts.bulkbar.rebalance")}
          </Button>
        </div>

        {/* Tag group */}
        <div className="flex max-w-full flex-wrap items-center gap-1.5">
          <span className="text-xs text-fg-muted">{t("accounts.bulkbar.tag_source")}</span>
          <Input
            value={sourceTag}
            onChange={(e) => setSourceTag(e.target.value)}
            placeholder={t("accounts.bulkbar.tag_placeholder")}
            className="h-8 min-w-[10rem] flex-1 sm:w-40 sm:flex-none"
          />
          <Button size="sm" variant="secondary" disabled={!sourceTag.trim() || busy} onClick={applySource}>
            {t("accounts.bulkbar.apply")}
          </Button>
        </div>

        {/* Inbox group — add/remove from the inbox poll pool */}
        <div className="flex max-w-full flex-wrap items-center gap-1.5">
          <span className="flex items-center gap-1 text-xs text-fg-muted">
            <Inbox size={14} strokeWidth={2} aria-hidden="true" />
            {t("accounts.bulkbar.inbox")}
          </span>
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => applyInbox(true)}>
            {t("accounts.bulkbar.inbox_on")}
          </Button>
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => applyInbox(false)}>
            {t("accounts.bulkbar.inbox_off")}
          </Button>
        </div>

        {/* Pause group — freeze selected accounts: stops warmup/maintenance so
            no proxy traffic is spent. Resume returns mid-warmup accounts to
            warming_up (progress is preserved). */}
        <div className="flex max-w-full flex-wrap items-center gap-1.5">
          <span className="flex items-center gap-1 text-xs text-fg-muted">
            <Pause size={14} strokeWidth={2} aria-hidden="true" />
            {t("accounts.bulkbar.pause_group")}
          </span>
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => applyStatus("paused")}>
            {t("accounts.pause")}
          </Button>
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => applyStatus("active")}>
            {t("accounts.resume")}
          </Button>
        </div>

        {/* Check — the row's "Проверить" for the whole selection, one account
            at a time in the background. One batch at a time. */}
        <Button
          size="sm"
          variant="outline"
          disabled={busy || overSweep || checkRunning}
          onClick={() => setConfirmCheck(true)}
          iconLeft={<RefreshCw size={15} strokeWidth={2} aria-hidden="true" />}
          title={checkRunning ? (t("accounts.bulk_check.running") ?? "") : undefined}
        >
          {t("accounts.test")}
        </Button>

        {/* Security sweep — throttled (waves of ≤15): kick seller sessions / set 2FA */}
        <div className="flex max-w-full flex-wrap items-center gap-1.5">
          <span className="text-xs text-fg-muted">{t("accounts.bulk_sweep.group")}</span>
          <Button
            size="sm"
            variant="outline"
            disabled={busy || overSweep}
            onClick={() => setConfirmKick(true)}
            iconLeft={<LockOpen size={15} strokeWidth={2} aria-hidden="true" />}
          >
            {t("accounts.export_session.bulk")}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={busy || overSweep}
            onClick={() => setConfirm2fa(true)}
            iconLeft={<Lock size={15} strokeWidth={2} aria-hidden="true" />}
          >
            {t("accounts.bulk_sweep.lock_2fa")}
          </Button>
        </div>

        {/* Danger group */}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="danger"
            disabled={busy}
            onClick={() => setConfirmDelete(true)}
            iconLeft={<Trash2 size={15} strokeWidth={2} aria-hidden="true" />}
          >
            {t("accounts.bulkbar.delete")}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onCleared}
            iconLeft={<X size={15} strokeWidth={2} aria-hidden="true" />}
          >
            {t("accounts.bulkbar.clear")}
          </Button>
        </div>
      </div>

      {(overBulk || overSweep) && (
        <p className="mt-2 text-xs text-warning">
          {overBulk
            ? t("accounts.bulkbar.too_many", { max: BULK_MAX })
            : t("accounts.bulkbar.too_many_sweep", { max: SWEEP_MAX })}
        </p>
      )}

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        tone="danger"
        title={t("accounts.bulkbar.delete_confirm", { count })}
        description={t("accounts.bulkbar.delete_confirm_body")}
        confirmText={t("accounts.bulkbar.delete")}
        cancelText={t("common.cancel")}
        onConfirm={onDelete}
      />

      <ConfirmDialog
        open={confirmCheck}
        onOpenChange={setConfirmCheck}
        title={t("accounts.bulk_check.confirm", { count })}
        description={t("accounts.bulk_check.confirm_body", {
          minutes: Math.max(1, Math.round(((count - 1) * CHECK_AVG_SECONDS) / 60)),
        })}
        confirmText={t("accounts.test")}
        cancelText={t("common.cancel")}
        onConfirm={onBulkCheck}
      />

      <ConfirmDialog
        open={confirmKick}
        onOpenChange={setConfirmKick}
        title={t("accounts.bulk_sweep.kick_confirm", { count })}
        description={t("accounts.bulk_sweep.throttle_note", { wave: 15 })}
        confirmText={t("accounts.export_session.bulk")}
        cancelText={t("common.cancel")}
        onConfirm={() => onBulkSweep("terminate_sessions")}
      />

      <ConfirmDialog
        open={confirm2fa}
        onOpenChange={setConfirm2fa}
        title={t("accounts.bulk_sweep.lock_2fa_confirm", { count })}
        description={t("accounts.bulk_sweep.throttle_note", { wave: 15 })}
        confirmText={t("accounts.bulk_sweep.lock_2fa")}
        cancelText={t("common.cancel")}
        onConfirm={() => onBulkSweep("lock_2fa")}
      />
    </div>
  );
}
